import { useEffect, useState, useRef } from 'react';
import { logDebug } from '../../utils/utils';

/**
 * Hook per gestire i valori di default condizionali per Railway forms
 * Cambia automaticamente i valori dei campi quando cambia railwayToDesign
 *
 * 🔧 FIX: Skip resetting values on initial load to preserve saved data
 */
export const useRailwayConditionalDefaults = (values: any, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, projectType: string) => {
    const [isApplyingDefaults, setIsApplyingDefaults] = useState(false);
    const previousRailwayToDesign = useRef<string | null>(null);
    const isInitialRender = useRef(true);

    useEffect(() => {
        // Solo per progetti Railway
        if (projectType !== 'railways') return;

        // Ottieni il valore corrente di railwayToDesign
        const railwayToDesign = values?.railwayType?.railwayToDesign;
        if (!railwayToDesign) return;

        // 🔧 FIX: Skip on initial render to preserve saved data
        // REASON: When loading a saved project, we want to keep the saved values (e.g., 800/150),
        // NOT reset them to defaults (500/60). Only apply defaults when user actively changes the railway type.
        if (isInitialRender.current) {
            logDebug('useRailwayConditionalDefaults', 'Initial render - skipping defaults application to preserve saved data', { railwayToDesign });
            previousRailwayToDesign.current = railwayToDesign;
            isInitialRender.current = false;
            return;
        }

        // 🔧 FIX: Only apply defaults if railwayToDesign actually changed
        // REASON: Prevents resetting values on every render
        if (previousRailwayToDesign.current === railwayToDesign) {
            return;
        }

        previousRailwayToDesign.current = railwayToDesign;

        logDebug('useRailwayConditionalDefaults', 'Railway type changed to:', railwayToDesign);

        // Definisci i valori per ogni tipo di treno
        const railwayDefaults = {
            cargo_train: {
                loadData: {
                    designAxleLoad: 500,
                    trainSpeed: 60,
                    wheelDiameter: 0.915,
                },
                trafficData: {
                    numberOfTrainsPerDay: 50,
                    numberOfCarsPerTrain: 10,
                    numberOfAxisPerCar: 4,
                    designLife: 20,
                },
            },
            passenger_train: {
                loadData: {
                    designAxleLoad: 170,
                    trainSpeed: 300,
                    wheelDiameter: 0.9,
                },
                trafficData: {
                    numberOfTrainsPerDay: 1,
                    numberOfCarsPerTrain: 10,
                    numberOfAxisPerCar: 4,
                    designLife: 20,
                },
            },
            mixed_train: {
                // For mixed_train, populate the duplicated fields
                loadData: {
                    designAxleLoadCargo: 500,
                    trainSpeedCargo: 60,
                    wheelDiameterCargo: 0.915,
                    designAxleLoadPassenger: 170,
                    trainSpeedPassenger: 300,
                    wheelDiameterPassenger: 0.9,
                },
                trafficData: {
                    numberOfTrainsPerDayCargo: 50,
                    numberOfCarsPerTrainCargo: 10,
                    numberOfAxisPerCarCargo: 4,
                    designLifeCargo: 20,
                    numberOfTrainsPerDayPassenger: 1,
                    numberOfCarsPerTrainPassenger: 10,
                    numberOfAxisPerCarPassenger: 4,
                    designLifePassenger: 20,
                },
            },
        };

        // Ottieni i valori di default per il tipo selezionato
        const defaults = railwayDefaults[railwayToDesign as keyof typeof railwayDefaults];
        if (!defaults) {
            logDebug('useRailwayConditionalDefaults', 'No defaults found for railway type:', railwayToDesign);
            return;
        }

        // Indica che stiamo applicando defaults automatici
        setIsApplyingDefaults(true);
        logDebug('useRailwayConditionalDefaults', 'Starting automatic defaults application', { railwayToDesign });

        // Aggiorna i valori dei campi loadData
        if (defaults.loadData) {
            Object.entries(defaults.loadData).forEach(([fieldName, value]) => {
                const currentValue = values?.loadData?.[fieldName];
                // Solo aggiorna se il valore è diverso per evitare loop infiniti
                if (currentValue !== value) {
                    logDebug('useRailwayConditionalDefaults', `Setting loadData.${fieldName} to:`, value);
                    setFieldValue(`loadData.${fieldName}`, value, false);
                }
            });
        }

        // Aggiorna i valori dei campi trafficData
        if (defaults.trafficData) {
            Object.entries(defaults.trafficData).forEach(([fieldName, value]) => {
                const currentValue = values?.trafficData?.[fieldName];
                // Solo aggiorna se il valore è diverso per evitare loop infiniti
                if (currentValue !== value) {
                    logDebug('useRailwayConditionalDefaults', `Setting trafficData.${fieldName} to:`, value);
                    setFieldValue(`trafficData.${fieldName}`, value, false);
                }
            });
        }

        // Reset del flag dopo un breve delay per permettere a Formik di processare i cambiamenti
        setTimeout(() => {
            setIsApplyingDefaults(false);
            logDebug('useRailwayConditionalDefaults', 'Finished automatic defaults application');
        }, 50);
    }, [values?.railwayType?.railwayToDesign, setFieldValue, projectType]);

    return { isApplyingDefaults };
};
